import { Deck, type SlidePosition } from "@hali-shares/deck-schema";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ScaledStage } from "../../camera/ScaledStage";
import { type PollUiState, PresenterContext, type PresenterCtx } from "../../deck/context";
import type { FlowLayout } from "../../deck/flow/layout";
import { layoutDeck } from "../../deck/flow/layouts";
import { type DeckModel, buildModel, nowTitle } from "../../deck/model";
import { SlideView } from "../../deck/SlideView";
import { ApiError, type StartedSession, api } from "../../lib/api";
import { usePresenterTheme } from "../../live/theme";
import { usePresenterSession } from "../../live/usePresenterSession";
import { type NotesMessage, type NotesState, openChannel } from "../../rehearse/channel";
import { sectionOf, total, totalBudgetMs } from "../../rehearse/clock";
import { useClock } from "../../rehearse/useClock";
import { clamp, next, prev, samePosition } from "../../nav/position";
import { ControlBar } from "./ControlBar";
import { Overview } from "./Overview";
import { TopBar } from "./TopBar";
import { useIdle } from "./useIdle";
import { useKeyboard } from "./useKeyboard";
import "./present.css";

type Loaded = {
  deckId: string;
  model: DeckModel;
  layouts: Map<string, FlowLayout>;
  joinUrl: string;
  /** null when rehearsing (?rehearse=1): no live session, poll controls are local. */
  session: StartedSession | null;
};

/** Position lives in the URL (?s=slide&k=stop, slide from 1) as well as on the server. */
function readUrlPosition(): SlidePosition | null {
  const q = new URLSearchParams(window.location.search);
  if (!q.has("s")) return null;
  return { slideIndex: Number(q.get("s")) - 1, stop: Number(q.get("k") ?? 0) };
}

function writeUrlPosition(pos: SlidePosition) {
  const url = new URL(window.location.href);
  url.searchParams.set("s", String(pos.slideIndex + 1));
  if (pos.stop) url.searchParams.set("k", String(pos.stop));
  else url.searchParams.delete("k");
  window.history.replaceState(null, "", url);
}

export function PresenterPage() {
  const { deckId = "" } = useParams();
  const [params] = useSearchParams();
  const rehearse = params.get("rehearse") === "1";
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [session, cfg] = await Promise.all([rehearse ? null : api.startSession(deckId), api.config()]);
        // A live session presents the version it froze at start; rehearsal shows the latest.
        const deck = Deck.parse(session ? session.spec : (await api.deck(deckId)).spec);
        const layouts = await layoutDeck(deck);
        const joinUrl = `${cfg.publicUrl.replace(/\/$/, "")}/j`;
        if (live) setLoaded({ deckId, model: buildModel(deck), layouts, joinUrl, session });
      } catch (e) {
        if (!live) return;
        if (e instanceof ApiError && e.status === 401) {
          navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`, {
            replace: true,
          });
        } else setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      live = false;
    };
  }, [deckId, rehearse, navigate]);

  if (error) return <p style={{ padding: 32 }}>Could not open this deck: {error}</p>;
  if (!loaded) return null;
  return <Presenter {...loaded} />;
}

function Presenter({ deckId, model, layouts, joinUrl, session: initialSession }: Loaded) {
  const { slides } = model;
  const [started, setStarted] = useState(initialSession);
  const session = usePresenterSession(started);
  const [initialUrlPos] = useState(readUrlPosition);
  // The position to push to the next session that connects (null = adopt the server's).
  const urlPos = useRef(initialUrlPos);
  const [pos, setPos] = useState<SlidePosition>(() => clamp(slides, initialUrlPos ?? { slideIndex: 0, stop: 0 }));
  // With a session and no ?s= in the URL, wait for the server's stored position before showing anything.
  const [ready, setReady] = useState(!started || initialUrlPos !== null);
  const [overview, setOverview] = useState(false);
  const [cursor, setCursor] = useState(pos.slideIndex);
  const [blank, setBlank] = useState(false);
  const [localPolls, setLocalPolls] = useState<Record<string, PollUiState>>({});
  const idle = useIdle(3000);
  const synced = useRef<string | null>(null);

  // On the first state from each session: adopt the stored position, or push ours if the URL named one.
  const state = session?.state;
  useEffect(() => {
    if (!state || !session || synced.current === state.code) return;
    synced.current = state.code;
    if (urlPos.current) session.goto(clamp(slides, urlPos.current));
    else setPos(clamp(slides, state.position));
    setReady(true);
  }, [state, session, slides]);

  useEffect(() => {
    if (ready) writeUrlPosition(pos);
  }, [pos, ready]);

  const slide = slides[pos.slideIndex]!;
  const block = slide.kind === "block" ? model.blocks.get(slide.blockId)?.block : undefined;
  const clock = useClock(deckId, sectionOf(slide), started?.code ?? null);
  const [theme, setTheme, cycleTheme] = usePresenterTheme();

  const sessionGoto = session?.goto;
  const clockStart = clock.start;
  const goto = useCallback(
    (p: SlidePosition) => {
      const c = clamp(slides, p);
      setPos((old) => (samePosition(old, c) ? old : c));
      setOverview(false);
      sessionGoto?.(c);
      clockStart(); // the clock starts with the first move
    },
    [slides, sessionGoto, clockStart],
  );

  const openNotes = useCallback(() => {
    window.open(`/present/${deckId}/notes`, `hali-shares-notes-${deckId}`, "popup,width=960,height=640");
  }, [deckId]);

  // Speaker notes window: send absolute state on every change (and every clock tick); obey its commands.
  const channel = useRef<BroadcastChannel | null>(null);
  const notesState = useMemo<NotesState>(() => {
    const sectionId = sectionOf(slide);
    const sec = model.sections.get(sectionId);
    const following = next(slides, pos);
    const nextSlide = samePosition(following, pos) ? null : slides[following.slideIndex];
    return {
      type: "state",
      slideIndex: pos.slideIndex,
      stop: pos.stop,
      slides: slides.length,
      nowTitle: nowTitle(model, pos.slideIndex, pos.stop),
      notes: block?.notes ?? null,
      nextTitle: nextSlide ? nowTitle(model, following.slideIndex, following.stop) : null,
      section: sec ? { number: sec.number, title: sec.section.title, budgetMs: sec.section.minutes * 60_000 } : null,
      sectionMs: clock.times[sectionId] ?? 0,
      totalMs: total(clock.times),
      totalBudgetMs: totalBudgetMs(model.deck),
      running: clock.running,
      started: clock.started,
      live: Boolean(session) && session?.state?.status !== "ENDED",
      code: session?.code ?? null,
    };
  }, [slide, slides, pos, model, block, clock.times, clock.running, clock.started, session]);
  const latestNotes = useRef(notesState);
  useEffect(() => {
    latestNotes.current = notesState;
    channel.current?.postMessage(notesState);
  }, [notesState]);

  const ctx = useMemo<PresenterCtx>(() => {
    const livePoll = (id: string): PollUiState => {
      const a = session?.state?.activeBlock;
      return a?.blockId === id ? { locked: a.locked, revealed: a.revealed } : { locked: false, revealed: false };
    };
    const ended = session?.state?.status === "ENDED";
    return {
      model,
      layouts,
      joinUrl,
      code: ended ? null : (session?.code ?? null),
      position: pos,
      goto,
      gotoReferences: () => {
        const i = slides.findIndex((s) => s.kind === "block" && s.type === "references");
        if (i >= 0) goto({ slideIndex: i, stop: 0 });
      },
      poll: (id) => (session ? livePoll(id) : (localPolls[id] ?? { locked: false, revealed: false })),
      togglePoll: (id, key) => {
        if (session) {
          if (ended) return;
          const cur = livePoll(id);
          if (key === "locked") session.setLocked(id, !cur.locked);
          else session.setRevealed(id, !cur.revealed);
          return;
        }
        setLocalPolls((all) => {
          const cur = all[id] ?? { locked: false, revealed: false };
          return { ...all, [id]: { ...cur, [key]: !cur[key] } };
        });
      },
      tally: (id) => session?.tallies[id],
      live: session
        ? {
            connected: session.connected,
            ended,
            end: session.end,
            restart: () => {
              synced.current = null;
              urlPos.current = pos;
              void api.startSession(deckId).then(setStarted);
            },
            csvUrl: `/api/sessions/${session.code}/responses.csv`,
          }
        : null,
      leaderboard: session?.leaderboard ?? null,
      clock: {
        started: clock.started,
        running: clock.running,
        times: clock.times,
        toggle: clock.toggle,
        reset: clock.reset,
      },
      theme,
      setTheme,
      cycleTheme,
      openNotes,
    };
  }, [
    model,
    layouts,
    joinUrl,
    goto,
    slides,
    localPolls,
    session,
    deckId,
    pos,
    clock.started,
    clock.running,
    clock.times,
    clock.toggle,
    clock.reset,
    theme,
    setTheme,
    cycleTheme,
    openNotes,
  ]);

  const step = (dir: 1 | -1) => {
    if (overview) return setCursor((c) => Math.min(slides.length - 1, Math.max(0, c + dir)));
    setBlank(false);
    goto((dir === 1 ? next : prev)(slides, pos));
  };
  const toggleOverview = () => {
    setCursor(pos.slideIndex);
    setOverview((o) => !o);
  };
  const onPoll = (key: keyof PollUiState) => {
    if (block?.type === "poll" || block?.type === "quiz") ctx.togglePoll(block.id, key);
  };

  const pinKeys = Object.fromEntries(
    Array.from({ length: 9 }, (_, i) => [
      String(i + 1),
      () => {
        if (block?.type === "screenshot" && i < block.pins.length) goto({ slideIndex: pos.slideIndex, stop: i + 1 });
      },
    ]),
  );

  useKeyboard({
    " ": () => step(1),
    ArrowRight: () => step(1),
    PageDown: () => step(1),
    ArrowLeft: () => step(-1),
    PageUp: () => step(-1),
    ArrowDown: () => overview && setCursor((c) => Math.min(slides.length - 1, c + 3)),
    ArrowUp: () => overview && setCursor((c) => Math.max(0, c - 3)),
    Enter: () => {
      if (overview) return goto({ slideIndex: cursor, stop: 0 });
      if (block?.type === "flow" && pos.stop === 0 && slide.kind === "block" && slide.stops > 1)
        goto({ slideIndex: pos.slideIndex, stop: 1 });
    },
    Escape: () => {
      if (overview) return setOverview(false);
      if (blank) return setBlank(false);
      if (pos.stop > 0) goto({ slideIndex: pos.slideIndex, stop: 0 });
    },
    o: toggleOverview,
    b: () => setBlank((b) => !b),
    r: () => onPoll("revealed"),
    l: () => onPoll("locked"),
    t: clock.toggle,
    n: openNotes,
    d: cycleTheme,
    ...pinKeys,
  });

  // Commands from the notes window run through the same handlers as the keyboard.
  const commands = useRef({ step, toggleTimer: clock.toggle, reset: clock.reset, blank: () => setBlank((b) => !b) });
  useEffect(() => {
    commands.current = { step, toggleTimer: clock.toggle, reset: clock.reset, blank: () => setBlank((b) => !b) };
  });
  useEffect(() => {
    const ch = openChannel(deckId);
    channel.current = ch;
    if (!ch) return;
    ch.onmessage = (e: MessageEvent<NotesMessage>) => {
      const m = e.data as NotesMessage | null;
      if (!m || typeof m !== "object") return;
      if (m.type === "hello") ch.postMessage(latestNotes.current);
      if (m.type !== "command") return;
      if (m.action === "next") commands.current.step(1);
      else if (m.action === "prev") commands.current.step(-1);
      else if (m.action === "toggle-timer") commands.current.toggleTimer();
      else if (m.action === "reset-timer") commands.current.reset();
      else if (m.action === "blank") commands.current.blank();
    };
    ch.postMessage(latestNotes.current);
    return () => {
      ch.close();
      channel.current = null;
    };
  }, [deckId]);

  if (!ready) return null;

  return (
    <PresenterContext.Provider value={ctx}>
      <ScaledStage>
        <main style={{ position: "absolute", inset: 0 }} data-slide-index={pos.slideIndex} data-stop={pos.stop}>
          {slide.kind !== "title" && <TopBar />}
          <SlideView key={pos.slideIndex} slide={slide} slideIndex={pos.slideIndex} stop={pos.stop} />
          {overview && (
            <Overview current={pos.slideIndex} cursor={cursor} onPick={(i) => goto({ slideIndex: i, stop: 0 })} />
          )}
          <ControlBar
            pos={pos}
            idle={idle}
            overviewOpen={overview}
            onOverview={toggleOverview}
            onPrev={() => step(-1)}
            onNext={() => step(1)}
          />
          {blank && <div className="blank" data-testid="blank" onClick={() => setBlank(false)} />}
        </main>
      </ScaledStage>
    </PresenterContext.Provider>
  );
}
